import React from "react";
import packageJson from "../package.json";

global.appVersion = packageJson.version;

// Cada cuánto se vuelve a preguntar por la versión publicada. La app es de
// sesión larga: chequear sólo al montar dejaba sin avisar al usuario que no
// recarga en todo el día, que es justamente el que queda atrás.
const INTERVALO_CHEQUEO_MS = 10 * 60 * 1000;

/**
 * Compara la versión compilada dentro del bundle (la de package.json) contra la
 * que quedó publicada en /meta.json, que el paso `prebuild` regenera en cada
 * build. Si no coinciden, el usuario está corriendo un bundle viejo.
 *
 * KAN-44: este componente estaba apagado desde el commit "buster off" (marzo de
 * 2020, a0767e5c) — quedó comentado en index.js y nunca volvió. Se rehabilita a
 * partir de la 7.0.0 para poder certificar en qué versión está el usuario
 * cuando reporta una denuncia o un reclamo.
 *
 * Dos cosas que la versión de 2020 no tenía y sin las cuales el chequeo no
 * sirve hoy:
 *
 * - El fetch va con `cache: "no-store"` y un query param distinto en cada
 *   llamada. Sin eso CloudFront le devuelve el meta.json viejo al mismo
 *   navegador que tiene el bundle viejo, los dos coinciden y el deploy nunca se
 *   detecta.
 * - Se repite cada INTERVALO_CHEQUEO_MS y al volver a la pestaña.
 *
 * No renderiza nada ni bloquea la app: avisa por `onOutdated` y quien lo usa
 * decide qué mostrar. La versión original envolvía a <App/> y no renderizaba
 * nada mientras cargaba, así que un meta.json que no respondía dejaba la
 * pantalla en blanco.
 */
class CacheBuster extends React.Component {
  constructor(props) {
    super(props);
    this.checkVersion = this.checkVersion.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.timer = null;
    // La versión que ya se avisó, para no repetir el aviso en cada chequeo.
    this.versionAvisada = null;
  }

  componentDidMount() {
    this.checkVersion();
    this.timer = setInterval(this.checkVersion, INTERVALO_CHEQUEO_MS);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  }

  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  }

  onVisibilityChange() {
    if (!document.hidden) this.checkVersion();
  }

  checkVersion() {
    fetch(`/meta.json?_=${Date.now()}`, { cache: "no-store" })
      .then(response => response.json())
      .then(meta => {
        const latestVersion = meta && meta.version;
        const currentVersion = global.appVersion;

        if (!latestVersion) return;
        if (latestVersion === currentVersion) return;
        if (latestVersion === this.versionAvisada) return;

        this.versionAvisada = latestVersion;
        if (this.props.onOutdated) {
          this.props.onOutdated({
            latestVersion,
            currentVersion,
            refreshCacheAndReload
          });
        }
      })
      .catch(e => {
        // Un meta.json que no responde no puede romper nada: el chequeo es
        // informativo, no una precondición para usar Bunkey.
        console.log("CacheBuster.checkVersion.e: ", e);
      });
  }

  render() {
    return null;
  }
}

/**
 * Limpia la caché del service worker y recarga. `caches.delete` es asíncrono:
 * recargar sin esperarlo dejaba servido el bundle viejo y el aviso volvía a
 * aparecer enseguida. También se desregistra el service worker, porque el de
 * CRA sirve el index.html precacheado y es el que deja pegado al usuario en una
 * versión vieja; index.js lo vuelve a registrar en la carga siguiente.
 */
export function refreshCacheAndReload() {
  const recargar = () => window.location.reload(true);
  const limpiarCaches = () => {
    if (typeof caches === "undefined") return Promise.resolve();
    return caches
      .keys()
      .then(names => Promise.all(names.map(name => caches.delete(name))));
  };
  const desregistrarSW = () => {
    if (!navigator.serviceWorker || !navigator.serviceWorker.getRegistrations) {
      return Promise.resolve();
    }
    return navigator.serviceWorker
      .getRegistrations()
      .then(regs => Promise.all(regs.map(reg => reg.unregister())));
  };

  try {
    desregistrarSW()
      .then(limpiarCaches)
      .then(recargar)
      .catch(recargar);
  } catch (error) {
    console.log("refreshCacheAndReload.e: ", error);
    recargar();
  }
}

export default CacheBuster;
